import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Check,
  Compass,
  Globe,
  KeyRound,
  Lock,
  MessageSquareText,
  Send,
  Users,
  X,
} from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import Prompt from "../components/Prompt";
import CommunityAvatar from "../components/CommunityAvatar";
import { communitiesAPI, connectionsAPI } from "../services/api";
import { getConnectionLimits } from "../constants/contentLimits";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import {
  mergeDiscoverRouteCache,
  readDiscoverRouteCache,
} from "../utils/discoverRouteCache";
import "./DiscoverCommunities.css";

const getJoinStateLabel = (joinState) => {
  if (joinState === "active") return "Already joined";
  if (joinState === "pending") return "Request pending";
  if (joinState === "invited") return "Invite pending";
  return "";
};

const getCardAction = ({
  community,
  isOwner,
  joiningCommunityId,
  navigate,
  setPendingPromptCommunity,
  communitiesSnapshot,
}) => {
  if (isOwner) {
    return {
      label: "Manage",
      disabled: false,
      onClick: () =>
        navigate(`/communities/${community._id}`, {
          state: {
            prefetchedCommunity: {
              ...community,
              canManage: true,
            },
          },
        }),
    };
  }

  if (community.joinState === "active") {
    return {
      label: "Member",
      disabled: false,
      onClick: () =>
        navigate(`/communities/${community._id}`, {
          state: {
            prefetchedCommunity: {
              ...community,
              canManage: false,
            },
          },
        }),
    };
  }

  if (community.joinState === "pending") {
    return {
      label: "Pending",
      disabled: true,
      onClick: null,
    };
  }

  if (community.joinState === "invited") {
    return {
      label: "View Invites",
      disabled: false,
      onClick: () =>
        navigate("/communities#my-invites", {
          state: communitiesSnapshot || undefined,
        }),
    };
  }

  return {
    label: joiningCommunityId === community._id ? "Joining..." : "Join",
    disabled: joiningCommunityId === community._id,
    onClick: () => setPendingPromptCommunity(community),
  };
};

const getJoinPromptText = (community) => {
  if (community.joinMode === "direct") {
    return `Join ${community.name} now? You will become a member immediately.`;
  }

  return `Send a join request to ${community.name}? The owner will need to approve it first.`;
};

const DiscoverCommunities = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const { user } = useAuthState();
  const connectionLimits = getConnectionLimits(user);
  const communitiesSnapshot = location.state?.fromCommunitiesSnapshot || null;
  const initialDiscoverCommunities =
    location.state?.prefetchedDiscoverCommunities ||
    readDiscoverRouteCache()?.discoverCommunities ||
    null;
  const [communities, setCommunities] = useState(
    initialDiscoverCommunities || [],
  );
  const [loading, setLoading] = useState(!initialDiscoverCommunities);
  const [joiningCommunityId, setJoiningCommunityId] = useState(null);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [pendingPromptCommunity, setPendingPromptCommunity] = useState(null);
  const [messageCommunity, setMessageCommunity] = useState(null);
  const [messageBody, setMessageBody] = useState("");
  const [messageSending, setMessageSending] = useState(false);
  const [messageStatus, setMessageStatus] = useState(null);

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: (
          <span className="discover-communities-breadcrumb-label">
            <Compass size={14} />
            <span>Discover Communities</span>
          </span>
        ),
        path: null,
      },
    ],
    [],
  );

  const handleBackToCommunities = React.useCallback(() => {
    navigate(communitiesSnapshot?.returnPath || "/communities", {
      state: communitiesSnapshot || undefined,
    });
  }, [communitiesSnapshot, navigate]);

  const loadDiscover = React.useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await communitiesAPI.getDiscover();
      const nextCommunities = response.data.communities || [];
      setCommunities(nextCommunities);
      mergeDiscoverRouteCache({ discoverCommunities: nextCommunities });
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to load discover communities",
        "error-warning",
      );
      handleBackToCommunities();
    } finally {
      setLoading(false);
    }
  }, [addToast, handleBackToCommunities]);

  useEffect(() => {
    loadDiscover(!initialDiscoverCommunities);
  }, [initialDiscoverCommunities, loadDiscover]);

  const handleJoinCommunity = async () => {
    if (!pendingPromptCommunity) return;

    try {
      const targetCommunity = pendingPromptCommunity;
      setJoiningCommunityId(targetCommunity._id);
      const response = await communitiesAPI.joinListedCommunity(
        targetCommunity._id,
      );
      addToast(
        response.data?.message || "Community join updated",
        "success-info",
      );
      const joinedStatus = response.data?.membership?.status;
      setPendingPromptCommunity(null);

      if (joinedStatus === "active") {
        navigate(`/communities/${targetCommunity._id}`, {
          state: {
            prefetchedCommunity: {
              ...targetCommunity,
              canManage: false,
            },
          },
        });
        return;
      }

      setCommunities((prev) =>
        prev.map((community) =>
          community._id === targetCommunity._id
            ? { ...community, joinState: joinedStatus || "pending" }
            : community,
        ),
      );
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to join this community",
        "error-warning",
      );
    } finally {
      setJoiningCommunityId(null);
    }
  };

  const handleCancelJoinRequest = async (community) => {
    if (!community.membershipId) return;

    try {
      setCancellingRequestId(community.membershipId);
      await communitiesAPI.cancelJoinRequest(community.membershipId);
      setCommunities((prev) =>
        prev.map((entry) =>
          entry._id === community._id
            ? { ...entry, joinState: "none", membershipId: null }
            : entry,
        ),
      );
      addToast("Join request cancelled", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to cancel join request",
        "error-warning",
      );
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handleOpenOwnerNote = async (community) => {
    setMessageCommunity(community);
    setMessageBody("");
    setMessageStatus(null);

    if (!community.ownerUserId) return;

    try {
      const response = await connectionsAPI.getTargetStatus(
        community.ownerUserId,
      );
      setMessageStatus(response.data);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Could not retrieve note allowance",
        "error-warning",
      );
    }
  };

  const handleSendOwnerNote = async () => {
    const trimmed = messageBody.trim();
    if (!messageCommunity || !trimmed) {
      addToast("Note is required", "error-warning");
      return;
    }

    try {
      setMessageSending(true);
      const response = await connectionsAPI.startConnection({
        targetUserId: messageCommunity.ownerUserId,
        body: trimmed,
        contextType: "community",
        communityId: messageCommunity._id,
      });
      const dailyLimit =
        response.data.noteLimit ||
        messageStatus?.dailyLimit ||
        connectionLimits.connectionDailyNotes;
      const sentToday =
        response.data.dailyUsage?.mine ||
        (messageStatus?.dailyUsage?.mine || 0) + 1;
      setMessageStatus((prev) => ({
        ...(prev || {}),
        dailyLimit,
        noteBodyLimit:
          prev?.noteBodyLimit || connectionLimits.connectionNoteBody,
        dailyUsage: { mine: sentToday },
        canSend: sentToday < dailyLimit,
      }));
      addToast("Note sent", "success-info");
      setMessageCommunity(null);
      setMessageBody("");
      window.dispatchEvent(new Event("inbox-changed"));
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to send note",
        "error-warning",
      );
    } finally {
      setMessageSending(false);
    }
  };

  const messageNoteBodyLimit =
    messageStatus?.noteBodyLimit || connectionLimits.connectionNoteBody;
  const messageDailyNoteLimit =
    messageStatus?.dailyLimit || connectionLimits.connectionDailyNotes;
  const messageDailyNotesUsed = messageStatus?.dailyUsage?.mine || 0;
  const messageDailyLimitReached = messageStatus
    ? !messageStatus.canSend
    : messageDailyNotesUsed >= messageDailyNoteLimit;

  if (loading && !communitiesSnapshot) {
    return <Spinner visible={true} />;
  }

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={
        <NavigationButtons
          hideExitButton={true}
          hideActiveButton={true}
          communityRouteState={communitiesSnapshot}
        />
      }
    >
      <div className="discover-communities-page">
        <section className="discover-communities-grid">
          {loading ? (
            <div className="discover-communities-empty">
              Loading listed communities...
            </div>
          ) : communities.length === 0 ? (
            <div className="discover-communities-empty">
              No listed communities are available right now.
            </div>
          ) : (
            communities.map((community) => {
              const joinStateLabel = getJoinStateLabel(community.joinState);
              const isOwner = community.joinState === "owner";
              const cardAction = getCardAction({
                community,
                isOwner,
                joiningCommunityId,
                navigate,
                setPendingPromptCommunity,
                communitiesSnapshot,
              });

              return (
                <article
                  className="discover-communities-card"
                  key={community._id}
                >
                  <div className="discover-communities-card-top">
                    <div className="discover-communities-card-badges">
                      <span>
                        <Users size={14} />
                        {community.activeMembersCount || 0}
                      </span>
                      <span>
                        <BookOpen size={14} />
                        {community.lessonsCount || 0}
                      </span>
                    </div>
                    <div className="discover-communities-mode">
                      {community.joinMode === "direct" ? (
                        <span className="discover-communities-mode-pill is-direct">
                          <Check size={14} />
                          Direct join
                        </span>
                      ) : (
                        <span className="discover-communities-mode-pill is-approval">
                          <Lock size={14} />
                          Approval required
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="discover-communities-title-row">
                    <CommunityAvatar
                      name={community.name}
                      to={`/communities/${community._id}/public-view`}
                    />
                    <h2>{community.name}</h2>
                  </div>
                  <p className="discover-communities-description">
                    {community.description || "Listed learning space."}
                  </p>

                  <div className="discover-communities-meta">
                    <span>
                      <Globe size={14} />
                      Listed community
                    </span>
                    <span>
                      <KeyRound size={14} />
                      Owner: {community.ownerDisplayName}
                    </span>
                  </div>

                  <div className="discover-communities-actions">
                    <Button
                      className="discover-communities-join-button"
                      onClick={cardAction.onClick}
                      disabled={cardAction.disabled}
                    >
                      {cardAction.label}
                    </Button>
                    {community.joinState === "pending" &&
                    community.membershipId ? (
                      <Button
                        className="discover-communities-cancel-button"
                        onClick={() => handleCancelJoinRequest(community)}
                        disabled={cancellingRequestId === community.membershipId}
                        title="Cancel join request"
                        aria-label="Cancel join request"
                      >
                        <X size={14} />
                      </Button>
                    ) : null}
                    {!isOwner && community.ownerUserId ? (
                      <Button
                        className="discover-communities-cancel-button"
                        onClick={() => {
                          handleOpenOwnerNote(community);
                        }}
                        title="Send note to owner"
                        aria-label="Send note to owner"
                      >
                        <MessageSquareText size={14} />
                      </Button>
                    ) : null}
                    {joinStateLabel ? (
                      <span className="discover-communities-state-pill">
                        {joinStateLabel}
                      </span>
                    ) : null}
                  </div>
                </article>
              );
            })
          )}
        </section>

        <section
          className="discover-communities-about"
          aria-labelledby="discover-communities-about-title"
        >
          <h2 id="discover-communities-about-title">
            About Discover Communities
          </h2>
          <p>
            Discover is for exploration. Browse listed communities and join the
            ones that interest you.
          </p>
        </section>
      </div>

      {pendingPromptCommunity && (
        <Prompt
          question={getJoinPromptText(pendingPromptCommunity)}
          onYes={handleJoinCommunity}
          onNo={() => setPendingPromptCommunity(null)}
          yesLabel={
            pendingPromptCommunity.joinMode === "direct"
              ? "Join now"
              : "Send request"
          }
          noLabel="Cancel"
        />
      )}
      {messageCommunity ? (
        <div
          className="discover-communities-note-overlay"
          onClick={() => setMessageCommunity(null)}
        >
          <div
            className="discover-communities-note-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="discover-communities-note-header">
              <div>
                <h2>Send note</h2>
                <p>
                  To {messageCommunity.ownerDisplayName} about{" "}
                  {messageCommunity.name}
                </p>
              </div>
              <Button
                className="discover-communities-cancel-button"
                onClick={() => setMessageCommunity(null)}
                title="Close"
                aria-label="Close"
              >
                <X size={14} />
              </Button>
            </div>
            <textarea
              value={messageBody}
              onChange={(event) => setMessageBody(event.target.value)}
              placeholder="Write a short note..."
              maxLength={messageNoteBodyLimit}
              rows="5"
              className="discover-communities-note-textarea"
              disabled={messageDailyLimitReached}
            />
            <div className="discover-communities-note-actions">
              <span
                className={
                  messageDailyLimitReached ? "is-limit-reached" : ""
                }
              >
                {messageBody.length}/{messageNoteBodyLimit} ·{" "}
                {messageDailyNotesUsed}/{messageDailyNoteLimit} today
              </span>
              <Button
                className="discover-communities-join-button"
                onClick={handleSendOwnerNote}
                disabled={messageSending || messageDailyLimitReached}
              >
                <Send size={14} />
                Send note
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </StudyLayout>
  );
};

export default DiscoverCommunities;
